<template>
  <div class="test-plan-create">
    <a-page-header 
      title="创建测试计划" 
      :sub-title="evalTargetLabel"
      @back="goBack"
    />

    <a-card style="margin-top: 16px">
      <a-steps :current="currentStep" style="margin-bottom: 24px">
        <a-step title="选择评估目标" />
        <a-step title="配置测试用例" />
        <a-step title="确认并创建" />
      </a-steps>

      <!-- 步骤1：选择评估目标 -->
      <div v-show="currentStep === 0" class="step-content">
        <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 16 }">
          <a-form-item label="评估目标" required>
            <a-radio-group v-model:value="formData.eval_target" @change="onEvalTargetChange">
              <a-space direction="vertical">
                <a-radio 
                  v-for="target in evalTargets" 
                  :key="target.value" 
                  :value="target.value"
                >
                  <div class="eval-target-option">
                    <strong>{{ target.label }}</strong>
                    <div class="description">{{ target.description }}</div>
                  </div>
                </a-radio>
              </a-space>
            </a-radio-group>
          </a-form-item>

          <a-form-item label="测试轮次" required>
            <a-input-number 
              v-model:value="formData.test_rounds" 
              :min="1" 
              :max="getMaxRounds"
            />
            <span class="hint" v-if="formData.eval_target === 'consistency'">
              （遵从性评估建议至少10轮）
            </span>
          </a-form-item>

          <a-form-item label="计划说明">
            <a-textarea 
              v-model:value="formData.eval_target_desc" 
              :rows="3"
              placeholder="描述本次测试的目标和范围"
            />
          </a-form-item>

          <a-form-item label="执行选项">
            <a-space direction="vertical" size="small">
              <a-checkbox v-model:checked="formData.disable_knowledge">
                禁用知识
              </a-checkbox>
              <div class="hint">执行时所有知识 ID 清空，不注入任何知识内容。</div>
              <a-checkbox v-model:checked="formData.auto_create_latest_snapshots">
                自动创建最新快照
              </a-checkbox>
              <div class="hint">创建计划时为当前选中的测试用例生成新快照并设为基线，本次计划固定使用这些新快照。</div>
            </a-space>
          </a-form-item>
        </a-form>
      </div>

      <!-- 步骤2：配置测试用例 -->
      <div v-show="currentStep === 1" class="step-content">
        <a-alert 
          :message="getConfigGuide" 
          type="info" 
          show-icon 
          style="margin-bottom: 16px"
        />

        <!-- 用例配置列表 -->
        <a-table
          :columns="planCaseColumns"
          :data-source="formData.plan_cases"
          :pagination="false"
          row-key="_key"
          size="small"
          :expandable="expandablePlanCase"
        >
          <template #expandedRowRender="{ record }">
            <div v-if="record._isPlanCase && record._stepsConfig?.length" class="step-config-expand">
              <a-table
                :columns="stepConfigColumns"
                :data-source="record._stepsConfig"
                :pagination="false"
                row-key="step_order"
                size="small"
                bordered
              >
                <template #bodyCell="{ column, record: step }">
                  <template v-if="column.key === 'snapshot'">
                    <a-select
                      :value="step.snapshot_id"
                      style="width: 200px"
                      :placeholder="formData.auto_create_latest_snapshots ? '创建时自动生成最新快照' : '选择快照'"
                      allow-clear
                      :disabled="formData.auto_create_latest_snapshots"
                      @change="(val) => onStepSnapshotChange(record, step.step_order, val)"
                    >
                      <a-select-option
                        v-for="s in (record._snapshotsByStep && record._snapshotsByStep[step.step_order]) || record._snapshots || []"
                        :key="s.id"
                        :value="s.id"
                      >
                        {{ s.snapshot_name }}
                      </a-select-option>
                    </a-select>
                  </template>
                  <template v-else-if="column.key === 'model'">
                    <a-select
                      :value="step.override_model_id"
                      style="width: 180px"
                      placeholder="使用快照默认"
                      allow-clear
                      :disabled="formData.eval_target !== 'model_comparison'"
                      @change="(val) => onStepModelChange(record, step.step_order, val)"
                    >
                      <a-select-option
                        v-for="m in models"
                        :key="m.id"
                        :value="m.id"
                      >
                        {{ m.model_name }}
                      </a-select-option>
                    </a-select>
                  </template>
                </template>
              </a-table>
            </div>
          </template>
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'test_case'">
              <a-select
                v-model:value="record.test_case_id"
                style="width: 200px"
                placeholder="选择测试用例"
                @change="(val) => onTestCaseChange(index, val)"
              >
                <a-select-option 
                  v-for="tc in testCases" 
                  :key="tc.id" 
                  :value="tc.id"
                >
                  {{ tc.case_name }}{{ tc.case_scope === 'plan' ? ` (整条${tc.steps_count ? tc.steps_count + '步' : ''})` : '' }}
                </a-select-option>
              </a-select>
            </template>

            <template v-else-if="column.key === 'snapshot'">
              <template v-if="record._isPlanCase">
                <a-tag color="purple">{{ record._snapshotName || '整条计划(按步快照)' }}</a-tag>
                <span class="expand-hint">（展开可配置每步快照/模型）</span>
              </template>
              <a-select
                v-else
                v-model:value="record.snapshot_id"
                style="width: 200px"
                :placeholder="formData.auto_create_latest_snapshots ? '创建时自动生成最新快照' : '选择快照'"
                :disabled="!record.test_case_id || formData.auto_create_latest_snapshots"
                @change="(val) => onSnapshotChange(index, val)"
              >
                <a-select-option 
                  v-for="snap in record._snapshots || []" 
                  :key="snap.id" 
                  :value="snap.id"
                >
                  {{ snap.snapshot_name }}
                </a-select-option>
              </a-select>
            </template>

            <template v-else-if="column.key === 'model'">
              <a-select
                v-model:value="record.override_model_id"
                style="width: 180px"
                placeholder="使用快照默认"
                allow-clear
                :disabled="formData.eval_target !== 'model_comparison'"
              >
                <a-select-option 
                  v-for="model in models" 
                  :key="model.id" 
                  :value="model.id"
                >
                  {{ model.model_name }}
                </a-select-option>
              </a-select>
            </template>

            <template v-else-if="column.key === 'snapshot_info'">
              <div v-if="formData.auto_create_latest_snapshots" class="snapshot-info">
                <div><strong>快照:</strong> 创建时自动生成</div>
                <div><strong>基线:</strong> 自动更新</div>
              </div>
              <div v-else-if="record._snapshotInfo" class="snapshot-info">
                <div><strong>模型:</strong> {{ record._snapshotInfo.model || 'N/A' }}</div>
                <div><strong>知识:</strong> {{ record._snapshotInfo.knowledge_count || 0 }}条</div>
              </div>
            </template>

            <template v-else-if="column.key === 'action'">
              <a-button 
                type="link" 
                danger 
                @click="removePlanCase(index)"
                :disabled="formData.plan_cases.length <= 1"
              >
                删除
              </a-button>
            </template>
          </template>
        </a-table>

        <a-button type="dashed" block style="margin-top: 16px" @click="addPlanCase">
          <PlusOutlined /> 添加配置
        </a-button>
      </div>

      <!-- 步骤3：确认并创建 -->
      <div v-show="currentStep === 2" class="step-content">
        <a-descriptions title="测试计划概览" bordered :column="2">
          <a-descriptions-item label="评估目标">
            {{ evalTargetLabel }}
          </a-descriptions-item>
          <a-descriptions-item label="测试轮次">
            {{ formData.test_rounds }}
          </a-descriptions-item>
          <a-descriptions-item label="配置数量">
            {{ formData.plan_cases.length }}
          </a-descriptions-item>
          <a-descriptions-item label="总任务数">
            {{ formData.plan_cases.length * formData.test_rounds }}
          </a-descriptions-item>
          <a-descriptions-item label="说明" :span="2">
            {{ formData.eval_target_desc || '无' }}
          </a-descriptions-item>
          <a-descriptions-item label="禁用知识">
            {{ formData.disable_knowledge ? '是' : '否' }}
          </a-descriptions-item>
          <a-descriptions-item label="自动创建最新快照">
            {{ formData.auto_create_latest_snapshots ? '是' : '否' }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 配置对比预览 -->
        <a-divider>配置对比</a-divider>
        <a-table
          :columns="previewColumns"
          :data-source="formData.plan_cases"
          :pagination="false"
          size="small"
        >
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'index'">
              配置 {{ index + 1 }}
            </template>
            <template v-else-if="column.key === 'model'">
              <a-tag :color="isVariableField('model') ? 'blue' : 'default'">
                {{ getEffectiveModel(record) }}
              </a-tag>
              <span v-if="isVariableField('model')" class="variable-marker">★变量</span>
            </template>
            <template v-else-if="column.key === 'knowledge'">
              {{ record._snapshotInfo?.knowledge_count || 0 }}条
            </template>
          </template>
        </a-table>

        <a-form-item label="计划名称" style="margin-top: 24px">
          <a-input 
            v-model:value="formData.plan_name" 
            placeholder="留空将自动生成"
            style="width: 400px"
          />
        </a-form-item>
      </div>

      <!-- 操作按钮 -->
      <div class="step-actions">
        <a-button v-if="currentStep > 0" @click="prevStep">上一步</a-button>
        <a-button v-if="currentStep < 2" type="primary" @click="nextStep">下一步</a-button>
        <a-button v-if="currentStep === 2" type="primary" :loading="submitting" @click="handleCreate">
          创建测试计划
        </a-button>
      </div>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import { createTestPlan, getEvalTargets } from '@/api/evalTestPlanV2';
import { getTestCases, getTestCaseSnapshots, getTestCaseDetail } from '@/api/evalTestCaseV2';
import { getModels } from '@/api/model';

const router = useRouter();
const currentStep = ref(0);
const submitting = ref(false);

// 数据
const evalTargets = ref([]);
const testCases = ref([]);
const models = ref([]);

// 表单数据
const formData = reactive({
  plan_name: '',
  eval_target: 'model_comparison',
  eval_target_desc: '',
  test_rounds: 5,
  disable_knowledge: false,
  auto_create_latest_snapshots: false,
  plan_cases: [{ _key: Date.now(), test_case_id: null, snapshot_id: null, override_model_id: null, step_snapshot_mapping: null, step_model_override: null, _stepsConfig: [] }]
});

// 列定义
const planCaseColumns = computed(() => {
  const cols = [
    { title: '#', key: 'index', width: 60, customRender: ({ index }) => index + 1 },
    { title: '测试用例', key: 'test_case', width: 220 },
    { title: '配置快照', key: 'snapshot', width: 220 },
  ];
  
  if (formData.eval_target === 'model_comparison') {
    cols.push({ title: '覆盖模型', key: 'model', width: 200 });
  }
  
  cols.push(
    { title: '快照信息', key: 'snapshot_info', width: 180 },
    { title: '操作', key: 'action', width: 80 }
  );
  
  return cols;
});

const previewColumns = [
  { title: '配置', key: 'index', width: 80 },
  { title: '测试用例', dataIndex: '_caseName', key: 'case_name', width: 200 },
  { title: '快照', dataIndex: '_snapshotName', key: 'snapshot', width: 200 },
  { title: '模型', key: 'model', width: 180 },
  { title: '知识数量', key: 'knowledge', width: 100 }
];

// 整条计划行可展开，展示每步快照/模型配置
const stepConfigColumns = [
  { title: '步骤', dataIndex: 'step_order', key: 'step_order', width: 80 },
  { title: 'Worker', dataIndex: 'worker_name', key: 'worker_name', width: 140 },
  { title: '快照', key: 'snapshot', width: 220 },
  { title: '模型', key: 'model', width: 180 }
];

const expandablePlanCase = computed(() => ({
  rowExpandable: (record) => record._isPlanCase && (record._stepsConfig?.length > 0)
}));

// 计算属性
const evalTargetLabel = computed(() => {
  const target = evalTargets.value.find(t => t.value === formData.eval_target);
  return target?.label || formData.eval_target;
});

const getMaxRounds = computed(() => {
  return formData.eval_target === 'consistency' ? 50 : 10;
});

const getConfigGuide = computed(() => {
  const guides = {
    consistency: '遵从性评估：添加1个配置，将执行多轮以评估输出一致性',
    model_comparison: '模型对比：添加多个配置，每个配置可指定不同的覆盖模型',
    knowledge_structure: '知识结构对比：添加同一用例的不同快照（不同提示词模板）',
    knowledge_content: '知识内容对比：添加同一用例的不同快照（不同知识配置）'
  };
  return guides[formData.eval_target] || '请添加测试配置';
});

// 方法
const goBack = () => {
  router.push('/eval-v2/test-plans');
};

const prevStep = () => {
  currentStep.value--;
};

const nextStep = () => {
  // 验证
  if (currentStep.value === 0) {
    if (!formData.eval_target) {
      message.warning('请选择评估目标');
      return;
    }
    if (formData.eval_target === 'consistency' && formData.test_rounds < 2) {
      message.warning('遵从性评估至少需要2轮');
      return;
    }
  }
  
  if (currentStep.value === 1) {
    const validCases = formData.plan_cases.filter(pc =>
      pc.test_case_id && (
        formData.auto_create_latest_snapshots ||
        pc.snapshot_id ||
        (pc.step_snapshot_mapping && Object.keys(pc.step_snapshot_mapping).length > 0)
      )
    );
    if (validCases.length === 0) {
      message.warning('请至少配置一个完整的测试用例');
      return;
    }
  }
  
  currentStep.value++;
};

const onEvalTargetChange = () => {
  // 根据评估目标调整默认轮次
  if (formData.eval_target === 'consistency') {
    formData.test_rounds = 10;
  } else {
    formData.test_rounds = 5;
  }
};

const addPlanCase = () => {
  formData.plan_cases.push({
    _key: Date.now(),
    test_case_id: null,
    snapshot_id: null,
    override_model_id: null,
    step_snapshot_mapping: null,
    step_model_override: null,
    _stepsConfig: []
  });
};

const removePlanCase = (index) => {
  formData.plan_cases.splice(index, 1);
};

const onTestCaseChange = async (index, testCaseId) => {
  const caseItem = formData.plan_cases[index];
  caseItem.snapshot_id = null;
  caseItem._snapshots = [];
  caseItem._snapshotInfo = null;
  caseItem.step_snapshot_mapping = null;
  caseItem._isPlanCase = false;

  const tc = testCases.value.find(t => t.id === testCaseId);
  caseItem._caseName = tc?.case_name || '';

  if (!testCaseId) return;

  if (tc?.case_scope === 'plan') {
    caseItem._isPlanCase = true;
    caseItem.step_model_override = caseItem.step_model_override || {};
    try {
      const detailRes = await getTestCaseDetail(testCaseId);
      const steps = (detailRes.data?.input_data?.steps || []).map((s, i) => ({
        step_order: s.step_order != null ? s.step_order : i + 1,
        worker_name: s.worker_name || `步骤${s.step_order != null ? s.step_order : i + 1}`,
        worker_id: s.worker_id || s.step_id,
        snapshot_id: (tc?.baseline_step_snapshot_mapping && tc.baseline_step_snapshot_mapping[String(s.step_order != null ? s.step_order : i + 1)]) || null,
        override_model_id: (caseItem.step_model_override && caseItem.step_model_override[String(s.step_order != null ? s.step_order : i + 1)]) || null
      }));
      caseItem._stepsConfig = steps;
      const mapping = {};
      const override = {};
      steps.forEach(st => {
        if (st.snapshot_id) mapping[String(st.step_order)] = st.snapshot_id;
        if (st.override_model_id) override[String(st.step_order)] = st.override_model_id;
      });
      caseItem.step_snapshot_mapping = Object.keys(mapping).length ? mapping : (tc?.baseline_step_snapshot_mapping ? { ...tc.baseline_step_snapshot_mapping } : {});
      const keys = Object.keys(caseItem.step_snapshot_mapping).sort((a, b) => Number(a) - Number(b));
      caseItem.snapshot_id = caseItem.step_snapshot_mapping[keys[0]];
      caseItem._snapshotName = `整条计划(${steps.length}步)`;
      caseItem._snapshotInfo = { model: '按步快照', knowledge_count: '-' };
    } catch (e) {
      console.error('获取整条计划步骤失败', e);
      caseItem.step_snapshot_mapping = tc?.baseline_step_snapshot_mapping ? { ...tc.baseline_step_snapshot_mapping } : {};
      const keys = Object.keys(caseItem.step_snapshot_mapping).sort((a, b) => Number(a) - Number(b));
      caseItem.snapshot_id = caseItem.step_snapshot_mapping[keys[0]];
      caseItem._snapshotName = `整条计划(${keys.length}步)`;
      caseItem._stepsConfig = keys.map((k, i) => ({ step_order: Number(k), worker_name: `步骤${k}`, snapshot_id: caseItem.step_snapshot_mapping[k], override_model_id: null }));
    }
    const snapRes = await getTestCaseSnapshots(testCaseId);
    if (snapRes.code === 200 && snapRes.data?.length) {
      caseItem._snapshots = snapRes.data;
      const byStep = {};
      (caseItem._stepsConfig || []).forEach(st => {
        byStep[st.step_order] = (caseItem._snapshots || []).filter(s => !st.worker_id || s.worker_id === st.worker_id);
        if (byStep[st.step_order].length === 0) byStep[st.step_order] = caseItem._snapshots;
      });
      caseItem._snapshotsByStep = byStep;
    }
    return;
  }

  try {
    const res = await getTestCaseSnapshots(testCaseId);
    if (res.code === 200) {
      caseItem._snapshots = res.data || [];
      if (caseItem._snapshots.length > 0) {
        caseItem.snapshot_id = caseItem._snapshots[0].id;
        onSnapshotChange(index, caseItem.snapshot_id);
      }
    }
  } catch (error) {
    console.error('获取快照列表失败:', error);
  }
};

const onStepSnapshotChange = (caseRecord, stepOrder, snapshotId) => {
  const step = caseRecord._stepsConfig?.find(s => s.step_order === stepOrder);
  if (step) step.snapshot_id = snapshotId || undefined;
  syncStepsToMapping(caseRecord);
};

const onStepModelChange = (caseRecord, stepOrder, modelId) => {
  const step = caseRecord._stepsConfig?.find(s => s.step_order === stepOrder);
  if (step) step.override_model_id = modelId || undefined;
  syncStepsToMapping(caseRecord);
};

function syncStepsToMapping(caseRecord) {
  const mapping = {};
  const override = {};
  (caseRecord._stepsConfig || []).forEach(st => {
    if (st.snapshot_id) mapping[String(st.step_order)] = st.snapshot_id;
    if (st.override_model_id) override[String(st.step_order)] = st.override_model_id;
  });
  caseRecord.step_snapshot_mapping = mapping;
  caseRecord.step_model_override = Object.keys(override).length ? override : null;
  const keys = Object.keys(mapping).sort((a, b) => Number(a) - Number(b));
  caseRecord.snapshot_id = mapping[keys[0]] || null;
}

const onSnapshotChange = (index, snapshotId) => {
  const caseItem = formData.plan_cases[index];
  const snapshot = caseItem._snapshots?.find(s => s.id === snapshotId);
  
  caseItem._snapshotName = snapshot?.snapshot_name || '';
  
  if (snapshot) {
    // 解析快照摘要
    let configSummary = snapshot.config_summary;
    if (typeof configSummary === 'string') {
      try {
        configSummary = JSON.parse(configSummary);
      } catch (e) {
        configSummary = {};
      }
    }
    
    caseItem._snapshotInfo = {
      model: configSummary?.model || snapshot.model_name || 'N/A',
      model_id: configSummary?.model_id,
      knowledge_count: configSummary?.knowledge_count || 0,
      prompt_template: configSummary?.prompt_template_name || 'N/A'
    };
  }
};

const isVariableField = (field) => {
  const mapping = {
    consistency: [],
    model_comparison: ['model'],
    knowledge_structure: ['snapshot', 'prompt_template'],
    knowledge_content: ['snapshot', 'knowledge']
  };
  return (mapping[formData.eval_target] || []).includes(field);
};

const getEffectiveModel = (record) => {
  if (record.override_model_id) {
    const model = models.value.find(m => m.id === record.override_model_id);
    return model?.model_name || `模型#${record.override_model_id}`;
  }
  return record._snapshotInfo?.model || 'N/A';
};

const handleCreate = async () => {
  const validCases = formData.plan_cases
    .filter(pc => pc.test_case_id && (
      formData.auto_create_latest_snapshots ||
      pc.snapshot_id ||
      (pc.step_snapshot_mapping && Object.keys(pc.step_snapshot_mapping).length > 0)
    ))
    .map((pc, index) => {
      const item = {
        test_case_id: pc.test_case_id,
        snapshot_id: pc.snapshot_id || null,
        override_model_id: pc.override_model_id || null,
        sort_order: index
      };
      if (pc.step_snapshot_mapping && Object.keys(pc.step_snapshot_mapping).length > 0) {
        item.step_snapshot_mapping = pc.step_snapshot_mapping;
        if (pc.step_model_override && Object.keys(pc.step_model_override).length > 0) {
          item.step_model_override = pc.step_model_override;
        }
        if (!item.snapshot_id) {
          const keys = Object.keys(pc.step_snapshot_mapping).sort((a, b) => Number(a) - Number(b));
          item.snapshot_id = pc.step_snapshot_mapping[keys[0]];
        }
      }
      return item;
    });

  if (validCases.length === 0) {
    message.error('请至少配置一个完整的测试用例');
    return;
  }

  submitting.value = true;
  try {
    const res = await createTestPlan({
      plan_name: formData.plan_name || 'auto',
      eval_target: formData.eval_target,
      eval_target_desc: formData.eval_target_desc,
      test_rounds: formData.test_rounds,
      disable_knowledge: formData.disable_knowledge,
      auto_create_latest_snapshots: formData.auto_create_latest_snapshots,
      plan_cases: validCases
    });

    if (res.code === 200) {
      message.success('测试计划创建成功');
      router.push('/eval-v2/test-plans');
    } else {
      message.error(res.message || '创建失败');
    }
  } catch (error) {
    message.error('创建失败：' + error.message);
  } finally {
    submitting.value = false;
  }
};

// 初始化
const fetchEvalTargets = async () => {
  try {
    const res = await getEvalTargets();
    if (res.code === 200) {
      evalTargets.value = res.data || [];
    }
  } catch (error) {
    // 使用默认值
    evalTargets.value = [
      { value: 'consistency', label: '遵从性评估', description: '评估同一配置下多次执行输出的一致性和稳定性' },
      { value: 'model_comparison', label: '模型对比', description: '对比不同AI模型在相同条件下的输出质量' },
      { value: 'knowledge_structure', label: '知识结构对比', description: '对比不同知识组织方式/提示词结构对输出的影响' },
      { value: 'knowledge_content', label: '知识内容对比', description: '对比不同知识内容/覆盖度对输出的影响' }
    ];
  }
};

const fetchTestCases = async () => {
  try {
    const res = await getTestCases({ status: 'active', pageSize: 100 });
    if (res.code === 200) {
      testCases.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取测试用例失败:', error);
  }
};

const fetchModels = async () => {
  try {
    const res = await getModels({ is_enabled: 1 });
    if (res.code === 200) {
      models.value = res.data?.list || res.data || [];
    }
  } catch (error) {
    console.error('获取模型列表失败:', error);
  }
};

onMounted(() => {
  fetchEvalTargets();
  fetchTestCases();
  fetchModels();
});
</script>

<style scoped>
.test-plan-create {
  padding: 24px;
}

.step-content {
  min-height: 400px;
  padding: 24px 0;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.eval-target-option {
  padding: 8px 0;
}

.eval-target-option .description {
  color: #888;
  font-size: 12px;
  margin-top: 4px;
}

.hint {
  color: #888;
  font-size: 12px;
  margin-left: 8px;
}

.snapshot-info {
  font-size: 12px;
  color: #666;
}

.snapshot-info div {
  line-height: 1.6;
}

.variable-marker {
  color: #1890ff;
  font-size: 12px;
  margin-left: 4px;
}

.expand-hint {
  font-size: 12px;
  color: #888;
  margin-left: 8px;
}

.step-config-expand {
  padding: 12px 24px;
  background: #fafafa;
}
</style>

